import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, useFormikContext } from "formik";
import Select from "react-select";

import { getSelectFieldStyles, SelectFieldOption } from "components/global/select-field-styles";

import { EmptyState } from "components/admin/ui";

import {
  ActionRow,
  CheckboxCard,
  CheckboxLabel,
  FieldGroup,
  FieldHelper,
  FieldLabel,
  FormGrid,
  FormStack,
  MetadataCollectionGrid,
  PrimaryButton,
  SecondaryButton,
  TextInput,
  TextareaInput,
  ValidationErrorText,
  WideField,
} from "./styles/admin-forms.styles";
import { validateCrudValues } from "./validation";
import { FlexibleCollectionField } from "./flexible-collection-field";
import { FilePickerField, ImagePickerField } from "./shared-form-fields";
import { CrudFieldConfig, CrudFieldContext, CrudFormRenderProps } from "./types";
import {
  coerceDynamicValue,
  getDefaultVisibleFields,
  getValueAtPath,
  normalizeDateTimeValue,
  normalizeDateValue,
  resolveClearedFieldPaths,
  resolveFieldOptions,
  resolveFieldState,
  setValueAtPath,
} from "./utils";

type KeyValueRow = { key: string; value: string };

const normalizeObjectRows = (value?: Record<string, any>): KeyValueRow[] => {
  const entries = Object.entries(value || {}).map(([key, current]) => ({
    key,
    value: String(current ?? ""),
  }));

  return entries.length ? entries : [{ key: "", value: "" }];
};

const buildObjectFromRows = (rows: Array<{ key: string; value: string }>) =>
  rows.reduce<Record<string, any>>((accumulator, row) => {
    const key = row.key.trim();

    if (!key) {
      return accumulator;
    }

    accumulator[key] = coerceDynamicValue(row.value);
    return accumulator;
  }, {});

type SelectOption = SelectFieldOption;

const selectFieldStyles = getSelectFieldStyles;

const isSameKeyValueObject = (left: Record<string, any>, right: Record<string, any>) => {
  const leftKeys = Object.keys(left);

  return (
    leftKeys.length === Object.keys(right).length &&
    leftKeys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(right, key) &&
        String(left[key]) === String(right[key]),
    )
  );
};

/**
 * Rows live in local state because blank keys cannot be represented in the submitted object,
 * so deriving them from Formik would discard every freshly added row.
 */
const KeyValueField: React.FC<{ errorMessage?: string; field: CrudFieldConfig }> = ({
  errorMessage,
  field,
}) => {
  const { setFieldTouched, setFieldValue, values } = useFormikContext<Record<string, any>>();
  const fieldValue = getValueAtPath(values, field.name) as Record<string, any> | undefined;
  const [rows, setRows] = useState<KeyValueRow[]>(() => normalizeObjectRows(fieldValue));
  const rowsRef = useRef(rows);
  const committedRef = useRef<Record<string, any>>(buildObjectFromRows(rows));

  useEffect(() => {
    const incoming = fieldValue || {};

    if (isSameKeyValueObject(incoming, committedRef.current)) {
      return;
    }

    const nextRows = normalizeObjectRows(incoming);
    rowsRef.current = nextRows;
    committedRef.current = buildObjectFromRows(nextRows);
    setRows(nextRows);
  }, [fieldValue]);

  const commitRows = (nextRows: KeyValueRow[]) => {
    rowsRef.current = nextRows;
    committedRef.current = buildObjectFromRows(nextRows);
    setRows(nextRows);
    setFieldTouched(field.name, true, false);
    setFieldValue(field.name, committedRef.current);
  };

  const updateRow = (index: number, patch: Partial<KeyValueRow>) =>
    commitRows(
      rowsRef.current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)),
    );

  const filledKeys = rows.map((row) => row.key.trim()).filter(Boolean);
  const hasDuplicateKeys = new Set(filledKeys).size !== filledKeys.length;

  return (
    <WideField>
      <FlexibleCollectionField
        addLabel="Add field"
        helper={field.helper}
        items={rows}
        label={field.label}
        onAdd={() => commitRows([...rowsRef.current, { key: "", value: "" }])}
        onRemove={(index) =>
          commitRows(rowsRef.current.filter((_, rowIndex) => rowIndex !== index))
        }
        renderRow={(row, index) => (
          <MetadataCollectionGrid>
            <TextInput
              aria-invalid={Boolean(errorMessage)}
              aria-label={`${field.label} key ${index + 1}`}
              onBlur={() => setFieldTouched(field.name, true, false)}
              onChange={(event) => updateRow(index, { key: event.target.value })}
              placeholder="Key"
              value={row.key}
            />
            <TextInput
              aria-invalid={Boolean(errorMessage)}
              aria-label={`${field.label} value ${index + 1}`}
              onBlur={() => setFieldTouched(field.name, true, false)}
              onChange={(event) => updateRow(index, { value: event.target.value })}
              placeholder="Value"
              value={row.value}
            />
          </MetadataCollectionGrid>
        )}
        rowSurface
      />
      {hasDuplicateKeys ? (
        <ValidationErrorText>
          Repeated keys are merged into one entry. Rename them to keep both values.
        </ValidationErrorText>
      ) : null}
      {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
    </WideField>
  );
};

const FormikFieldRenderer: React.FC<{
  field: CrudFieldConfig;
  context?: CrudFieldContext;
}> = ({ context, field }) => {
  const { errors, values, setFieldTouched, setFieldValue, setValues, submitCount, touched } =
    useFormikContext<Record<string, any>>();

  if (resolveFieldState(field.hidden, values)) {
    return null;
  }

  const kind = field.kind || "text";
  const disabled = resolveFieldState(field.disabled, values);
  const value = getValueAtPath(values, field.name);
  const options = resolveFieldOptions(field.options, values, context);
  const fieldError = getValueAtPath(errors, field.name);
  const fieldTouched = getValueAtPath(touched as Record<string, any>, field.name);
  const errorMessage =
    typeof fieldError === "string" && (fieldTouched || submitCount > 0) ? fieldError : undefined;

  if (kind === "key_value") {
    return <KeyValueField errorMessage={errorMessage} field={field} />;
  }

  if (kind === "image") {
    return (
      <WideField>
        <ImagePickerField
          helper={field.helper}
          label={field.label}
          onChange={(nextValue) => {
            setFieldTouched(field.name, true, false);
            setFieldValue(field.name, nextValue);
          }}
          placeholder={field.placeholder}
          value={value}
        />
        {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
      </WideField>
    );
  }

  if (kind === "file") {
    return (
      <WideField>
        <FilePickerField
          accept={field.accept}
          helper={field.helper}
          label={field.label}
          onChange={(nextValue) => {
            setFieldTouched(field.name, true, false);
            setFieldValue(field.name, nextValue);
          }}
          placeholder={field.placeholder}
          value={value}
        />
        {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
      </WideField>
    );
  }

  if (kind === "object_array") {
    const items = Array.isArray(value) ? value : [];
    const nestedFields = field.itemFields || [];

    return (
      <WideField>
        <FlexibleCollectionField
          addLabel={field.addLabel || `Add ${field.label.toLowerCase()}`}
          cardLayout
          helper={field.helper}
          items={items}
          label={field.label}
          onAdd={() => {
            setFieldTouched(field.name, true, false);
            setFieldValue(field.name, [...items, field.createItem ? field.createItem() : {}]);
          }}
          onRemove={(index) => {
            setFieldTouched(field.name, true, false);
            setFieldValue(
              field.name,
              items.filter((_, itemIndex) => itemIndex !== index),
            );
          }}
          renderItemTitle={(_, index) => `${field.label} ${index + 1}`}
          renderRow={(item, index) =>
            nestedFields.length ? (
              nestedFields.map((nestedField) => {
                const nestedPath = `${field.name}.${index}.${nestedField.name}`;

                return (
                  <FormikFieldRenderer
                    context={{ index, item }}
                    field={{ ...nestedField, name: nestedPath }}
                    key={nestedPath}
                  />
                );
              })
            ) : (
              <EmptyState
                description="No nested fields are configured for this array item."
                title="Nothing to edit"
              />
            )
          }
          disableRemove={(rows) => rows.length === 1}
        />
        {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
      </WideField>
    );
  }

  if (kind === "checkbox") {
    return (
      <FieldGroup>
        <CheckboxCard>
          <input
            aria-invalid={Boolean(errorMessage)}
            checked={Boolean(value)}
            disabled={disabled}
            onBlur={() => setFieldTouched(field.name, true, false)}
            onChange={(event) => {
              setFieldTouched(field.name, true, false);
              setFieldValue(field.name, event.target.checked);
            }}
            type="checkbox"
          />
          <CheckboxLabel>{field.label}</CheckboxLabel>
        </CheckboxCard>
        {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
      </FieldGroup>
    );
  }

  if (kind === "textarea") {
    return (
      <WideField>
        <FieldGroup>
          <FieldLabel htmlFor={field.name}>{field.label}</FieldLabel>
          {field.helper ? <FieldHelper>{field.helper}</FieldHelper> : null}
          <TextareaInput
            aria-invalid={Boolean(errorMessage)}
            disabled={disabled}
            id={field.name}
            onBlur={() => setFieldTouched(field.name, true, false)}
            onChange={(event) => setFieldValue(field.name, event.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={field.rows || 4}
            value={value ?? ""}
          />
          {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
        </FieldGroup>
      </WideField>
    );
  }

  if (kind === "select") {
    const normalizedOptions: SelectOption[] = options.map((option) => ({
      isDisabled: option.disabled,
      label: option.label,
      value: String(option.value),
    }));
    const selectedOption =
      normalizedOptions.find((option) => option.value === String(value ?? "")) || null;

    return (
      <FieldGroup>
        <FieldLabel htmlFor={field.name}>{field.label}</FieldLabel>
        {field.helper ? <FieldHelper>{field.helper}</FieldHelper> : null}
        <Select
          aria-invalid={Boolean(errorMessage)}
          classNamePrefix="crud-select"
          inputId={field.name}
          isClearable={!field.required}
          isDisabled={disabled}
          isSearchable
          menuPlacement="auto"
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          onBlur={() => setFieldTouched(field.name, true, false)}
          onChange={(option) => {
            const nextValue = option?.value ?? "";
            const clearedPaths =
              nextValue === String(value ?? "")
                ? []
                : resolveClearedFieldPaths(field.clearFieldsOnChange, nextValue, values);

            setFieldTouched(field.name, true, false);

            if (!clearedPaths.length) {
              setFieldValue(field.name, nextValue);
              return;
            }

            setValues(
              clearedPaths.reduce(
                (nextValues, path) => setValueAtPath(nextValues, path, ""),
                setValueAtPath(values, field.name, nextValue),
              ),
            );
          }}
          options={normalizedOptions}
          placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
          styles={selectFieldStyles(Boolean(errorMessage))}
          value={selectedOption}
        />
        {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
      </FieldGroup>
    );
  }

  const type =
    kind === "email"
      ? "email"
      : kind === "number"
      ? "number"
      : kind === "date"
      ? "date"
      : kind === "datetime-local"
      ? "datetime-local"
      : "text";

  const normalizedValue =
    kind === "date"
      ? normalizeDateValue(value)
      : kind === "datetime-local"
      ? normalizeDateTimeValue(value)
      : value ?? "";

  return (
    <FieldGroup>
      <FieldLabel htmlFor={field.name}>{field.label}</FieldLabel>
      {field.helper ? <FieldHelper>{field.helper}</FieldHelper> : null}
      <TextInput
        as="input"
        aria-invalid={Boolean(errorMessage)}
        disabled={disabled}
        id={field.name}
        max={field.max}
        min={field.min}
        onBlur={() => setFieldTouched(field.name, true, false)}
        onChange={(event) =>
          setFieldValue(
            field.name,
            kind === "number"
              ? event.target.value === ""
                ? ""
                : event.target.value
              : event.target.value,
          )
        }
        placeholder={field.placeholder}
        required={field.required}
        step={field.step}
        type={type}
        value={normalizedValue}
      />
      {errorMessage ? <ValidationErrorText>{errorMessage}</ValidationErrorText> : null}
    </FieldGroup>
  );
};

const FormikCrudFormBody: React.FC<
  Pick<CrudFormRenderProps, "fields" | "isSubmitting" | "onCancel" | "submitLabel">
> = ({ fields = [], isSubmitting = false, onCancel, submitLabel }) => {
  const {
    handleSubmit,
    values,
    isSubmitting: formikSubmitting,
  } = useFormikContext<Record<string, any>>();
  const visibleFields = useMemo(() => getDefaultVisibleFields(fields, values), [fields, values]);

  return (
    <form onSubmit={handleSubmit}>
      <FormStack>
        <FormGrid>
          {visibleFields.map((field) => (
            <FormikFieldRenderer field={field} key={field.name} />
          ))}
        </FormGrid>

        <ActionRow>
          <SecondaryButton onClick={onCancel} type="button">
            Cancel
          </SecondaryButton>
          <PrimaryButton disabled={isSubmitting || formikSubmitting} type="submit">
            {isSubmitting || formikSubmitting ? "Saving..." : submitLabel}
          </PrimaryButton>
        </ActionRow>
      </FormStack>
    </form>
  );
};

export const CrudForm: React.FC<CrudFormRenderProps> = ({
  fields = [],
  initialValues,
  isSubmitting = false,
  onCancel,
  onSubmit,
  submitLabel,
}) => (
  <Formik
    enableReinitialize
    initialValues={initialValues}
    validate={(values) => validateCrudValues(fields, values)}
    onSubmit={async (values) => {
      await Promise.resolve(onSubmit(values));
    }}
  >
    <FormikCrudFormBody
      fields={fields}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      submitLabel={submitLabel}
    />
  </Formik>
);
